const MDX_FLAG_KEY = 'dex-tech-fe-mdx-enabled';

// Static context for a global server-side kill switch. If we later want
// per-user or per-merchant rollout, thread a richer context through here.
const SERVER_CONTEXT = { key: 'dex-tech-fe-server' };

interface MdxFlagPayload {
  enabled: boolean;
  // Optional list of slugs to force-fall-through to Contentful even when
  // `enabled` is true. Lets us yank a single broken page without disabling
  // the whole MDX rollout.
  blockedSlugs?: string[];
}

const DEFAULT_PAYLOAD: MdxFlagPayload = { enabled: false, blockedSlugs: [] };

function normalizePayload(value: unknown): MdxFlagPayload {
  if (typeof value === 'boolean') {
    // Tolerate the legacy boolean flag shape during the LD migration window.
    return { enabled: value, blockedSlugs: [] };
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return {
      enabled: Boolean(obj.enabled),
      blockedSlugs: Array.isArray(obj.blockedSlugs)
        ? (obj.blockedSlugs as unknown[]).filter(
            (entry): entry is string => typeof entry === 'string'
          )
        : [],
    };
  }
  return DEFAULT_PAYLOAD;
}

/**
 * Returns whether MDX-sourced rendering is enabled for the given slug.
 *
 * Flag shape (LaunchDarkly JSON variation):
 *   { "enabled": true, "blockedSlugs": ["build-basics/foo"] }
 *
 * - Server-side: consults LaunchDarkly. Defaults to false on any error so
 *   Contentful continues to serve /docs/*.
 * - Client-side (browser): returns true and lets the payload-fetch call
 *   determine availability. Server-side flag flips only take effect for
 *   subsequent full page loads; mid-session client navigation may briefly
 *   render MDX after a flag flip-off — acceptable V1 trade-off because the
 *   transition window is short.
 * - Non-production: MDX_ENABLED_OVERRIDE=true forces on for local dev/tests.
 *
 * The LaunchDarkly wrapper is dynamically imported to keep its
 * server-only dependencies (fs, @sentry/serverless, container-exceptions)
 * out of the client bundle.
 */
async function isMdxEnabledForSlug(slug: string): Promise<boolean> {
  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.MDX_ENABLED_OVERRIDE === 'true'
  ) {
    return true;
  }

  if (typeof window !== 'undefined') {
    return true;
  }

  try {
    // eslint-disable-next-line @nx/enforce-module-boundaries
    const { initOrGetFeatureClient } = await import(
      '@squareup/dex-utils-shared-feature-flags-client-wrapper'
    );
    const client = await initOrGetFeatureClient();
    if (!client) {
      return false;
    }
    const raw = await client.getFlagValue(
      MDX_FLAG_KEY,
      SERVER_CONTEXT,
      DEFAULT_PAYLOAD
    );
    const payload = normalizePayload(raw);
    if (!payload.enabled) {
      return false;
    }
    return !(payload.blockedSlugs || []).includes(slug);
  } catch {
    return false;
  }
}

export { isMdxEnabledForSlug, MDX_FLAG_KEY };
export type { MdxFlagPayload };
